import React, {useState, useRef, useEffect} from 'react'
import styled from 'styled-components'
import {ChevronDown} from 'lucide-react'

interface CustomSelectProps {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  disabled?: boolean
}

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #495057;
  font-size: 12px;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
`

const SelectButton = styled.button<{isOpen: boolean; disabled?: boolean}>`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #c8d6fc;
  background: ${(props) => (props.disabled ? '#f8f9fa' : '#ffffff')};
  color: ${(props) => (props.disabled ? '#6c757d' : '#212529')};
  font-size: 1rem;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  text-align: left;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(props) => (props.disabled ? '#c8d6fc' : '#03a9fc')};
  }

  &:hover {
    border-color: ${(props) => (props.disabled ? '#c8d6fc' : '#03a9fc')};
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #03a9fc;
`

const DropdownMenu = styled.div<{isOpen: boolean}>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  border: 1px solid #c8d6fc;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 240px;
  overflow-y: auto;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
`

const DropdownItem = styled.button<{selected: boolean}>`
  width: 100%;
  padding: 12px;
  border: none;
  background: ${(props) => (props.selected ? '#e3f2fd' : '#ffffff')};
  color: #212529;
  font-size: 1rem;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f0f7ff;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`

export const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: string) => {
    onChange(option)
    setIsOpen(false)
  }

  return (
    <SelectContainer ref={containerRef}>
      <Label>{label}</Label>
      <SelectButton
        type="button"
        isOpen={isOpen}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}>
        <span>{value}</span>
        <IconWrapper>
          <ChevronDown size={20} />
        </IconWrapper>
      </SelectButton>
      <DropdownMenu isOpen={isOpen}>
        {options.map((option) => (
          <DropdownItem
            key={option}
            selected={option === value}
            onClick={() => handleSelect(option)}>
            {option}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </SelectContainer>
  )
}
